<template>
  <b-overlay :show="isLoading" rounded="sm">
    <template #overlay>
      <div class="text-center">
        <digger-loader />
      </div>
    </template>

    <div class="p-relative header-image pb-5"></div>

    <b-container class="custom-container mt-5">
      <b-row>
        <b-col cols="12" class="px-3">
          <open-to-work-tabs />

          <h4 class="font-weight-bold pt-4 pb-1 align-middle">Jobs</h4>
          <p class="text-muted mb-4">
            Work advertised by companies on TradiePass.
          </p>

          <b-row class="job-filters">
            <b-col md="6" lg="4" class="mb-3">
              <b-form-input
                v-model="search"
                type="search"
                placeholder="Search jobs"
                @input="onSearchInput"
                @keyup.enter="onSearchNow"
              />
            </b-col>
            <b-col sm="4" lg="2" class="mb-3">
              <b-form-select
                v-model="tradeId"
                :options="tradeOptions"
                @change="onFilterChanged"
              />
            </b-col>
            <b-col sm="4" lg="2" class="mb-3">
              <b-form-select
                v-model="state"
                :options="stateOptions"
                @change="onFilterChanged"
              />
            </b-col>
            <b-col sm="4" lg="2" class="mb-3">
              <b-form-select
                v-model="employmentType"
                :options="employmentTypeOptions"
                @change="onFilterChanged"
              />
            </b-col>
          </b-row>

          <template v-if="jobs.length">
            <p v-if="jobsMeta && jobsMeta.total" class="job-count">
              {{ jobsMeta.total }} job{{ jobsMeta.total === 1 ? '' : 's' }}
            </p>

            <div class="job-grid">
              <div
                v-for="job in jobs"
                :key="job.id"
                class="job-card"
                role="button"
                tabindex="0"
                @click="goToJob(job)"
                @keyup.enter="goToJob(job)"
              >
                <div class="job-card-head">
                  <b-avatar
                    :src="job.company.logo_url || undefined"
                    :text="initials(job.company.name)"
                    square
                    size="48px"
                    class="job-card-logo"
                  />
                  <div class="job-card-heading">
                    <div class="job-title">{{ job.title }}</div>
                    <div class="job-card-company">{{ job.company.name }}</div>
                  </div>
                  <b-badge
                    v-if="job.has_applied"
                    :variant="tradieStatusVariant(myApplicationStatus(job))"
                    pill
                  >
                    {{ tradieStatusText(myApplicationStatus(job)) }}
                  </b-badge>
                  <b-badge
                    v-else-if="job.has_withdrawn"
                    variant="secondary"
                    pill
                  >
                    Withdrawn · Reapply
                  </b-badge>
                </div>

                <div class="job-card-chips">
                  <span v-if="job.trade" class="job-chip job-chip--trade">
                    {{ job.trade.name }}
                  </span>
                  <span class="job-chip">
                    {{ employmentTypeLabel(job.employment_type) }}
                  </span>
                  <span v-if="job.roster" class="job-chip">
                    Roster {{ rosterLabel(job.roster) }}
                  </span>
                  <span v-if="job.experience_level" class="job-chip">
                    {{ experienceLevelLabel(job.experience_level) }}
                  </span>
                  <span v-if="job.accommodation_provided" class="job-chip">
                    Accommodation
                  </span>
                  <span v-if="job.travel_provided" class="job-chip">
                    Travel
                  </span>
                  <span v-if="job.start_date" class="job-chip">
                    Starts {{ formatDate(job.start_date) }}
                  </span>
                  <span
                    v-for="ticket in visibleTickets(job)"
                    :key="ticket"
                    class="job-chip job-chip--ticket"
                  >
                    {{ ticket }}
                  </span>
                  <span v-if="hiddenTicketCount(job)" class="job-chip">
                    +{{ hiddenTicketCount(job) }} more
                  </span>
                </div>

                <div class="job-card-foot">
                  <div class="job-card-location">
                    <b-icon icon="geo-alt-fill" class="mr-1" />{{
                      locationLabel(job)
                    }}
                  </div>
                  <div class="job-card-pay" :class="{ 'is-hidden': !job.pay }">
                    {{ job.pay ? payLabel(job) : 'Pay not shown' }}
                  </div>
                </div>
                <div class="job-card-posted">
                  Posted {{ timeAgo(job.published_at) }}
                </div>
              </div>
            </div>
          </template>

          <p v-else-if="!isLoading" class="text-muted py-5 text-center">
            {{ emptyMessage }}
          </p>

          <b-pagination
            v-if="jobsMeta && jobsMeta.total > perPage"
            v-model="currentPage"
            :total-rows="jobsMeta.total"
            :per-page="perPage"
            align="center"
            class="mt-3"
            @change="onPageChanged"
          />
        </b-col>
      </b-row>
    </b-container>
  </b-overlay>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import OpenToWorkTabs from '@/components/openToWork/OpenToWorkTabs.vue'
import apiToasts from '@/mixins/apiToasts'
import {
  AU_STATES,
  EMPLOYMENT_TYPES,
  employmentTypeLabel,
  experienceLevelLabel,
  formatDate,
  locationLabel,
  myApplicationStatus,
  payLabel,
  rosterLabel,
  timeAgo,
  tradieStatusText,
  tradieStatusVariant,
} from '@/utils/jobPostings'

// Tickets shown on a card before "+N more" (the job page lists them all).
const CARD_TICKET_LIMIT = 2

export default {
  name: 'OpenToWorkJobsPage',

  components: { OpenToWorkTabs },

  mixins: [apiToasts],

  data() {
    return {
      isLoading: false,
      search: '',
      tradeId: null,
      state: null,
      employmentType: null,
      currentPage: 1,
      perPage: 12,
      searchTimer: null,
    }
  },

  computed: {
    ...mapGetters('jobBoard', ['jobs', 'jobsMeta', 'trades']),

    tradeOptions() {
      return [
        { value: null, text: 'All trades' },
        ...this.trades.map((t) => ({ value: t.id, text: t.name })),
      ]
    },
    stateOptions() {
      return [
        { value: null, text: 'All states' },
        ...AU_STATES.map((s) => ({ value: s, text: s })),
      ]
    },
    employmentTypeOptions() {
      return [{ value: null, text: 'All types' }, ...EMPLOYMENT_TYPES]
    },
    hasFilters() {
      return !!(
        this.search.trim() ||
        this.tradeId ||
        this.state ||
        this.employmentType
      )
    },
    emptyMessage() {
      return this.hasFilters
        ? 'No jobs match these filters.'
        : 'There are no jobs posted right now. Check back soon.'
    },
  },

  async created() {
    this.fetchTrades().catch(() => {})
    await this.loadJobs()
  },

  beforeDestroy() {
    clearTimeout(this.searchTimer)
  },

  methods: {
    ...mapActions('jobBoard', ['fetchJobs', 'fetchTrades']),

    employmentTypeLabel,
    experienceLevelLabel,
    formatDate,
    myApplicationStatus,
    rosterLabel,
    tradieStatusText,
    tradieStatusVariant,

    visibleTickets(job) {
      return (job.required_tickets || []).slice(0, CARD_TICKET_LIMIT)
    },

    hiddenTicketCount(job) {
      return Math.max(
        (job.required_tickets || []).length - CARD_TICKET_LIMIT,
        0,
      )
    },
    locationLabel,
    payLabel,
    timeAgo,

    initials(name) {
      return (name || '')
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part[0].toUpperCase())
        .join('')
    },

    async loadJobs() {
      this.isLoading = true
      try {
        await this.fetchJobs({
          filters: {
            q: this.search.trim(),
            trade_id: this.tradeId,
            state: this.state,
            employment_type: this.employmentType,
          },
          page: this.currentPage,
          perPage: this.perPage,
        })
      } catch (err) {
        this.onError(err)
      } finally {
        this.isLoading = false
      }
    },

    onFilterChanged() {
      this.currentPage = 1
      this.loadJobs()
    },

    // Wait for a pause in typing so each keystroke is not a request. Emptying
    // the box is a deliberate "show everything", so that goes straight through.
    onSearchInput() {
      clearTimeout(this.searchTimer)
      if (!this.search.trim()) return this.onFilterChanged()
      this.searchTimer = setTimeout(this.onFilterChanged, 500)
    },

    // Enter skips the wait.
    onSearchNow() {
      clearTimeout(this.searchTimer)
      this.onFilterChanged()
    },

    onPageChanged(page) {
      this.currentPage = page
      this.loadJobs()
    },

    goToJob(job) {
      this.$router.push({
        name: 'open-to-work.job-page',
        params: { jobId: job.id },
      })
    },
  },
}
</script>

<style lang="scss" scoped>
// Gray values follow the newer Tailwind-style scale in DESIGN.md.
.job-count {
  font-size: 0.875rem;
  color: #6b7280;
  margin-bottom: 0.75rem;
}

// Two columns on desktop so the page is not one long thin strip.
.job-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1rem;
}

@media (max-width: 767.98px) {
  .job-grid {
    grid-template-columns: 1fr;
  }
}

.job-card {
  display: flex;
  flex-direction: column;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 1.25rem;
  cursor: pointer;
  // Resting card shadow from DESIGN.md.
  box-shadow: 0px 10px 25px #00429d0d;
  transition: border-color 0.15s ease, box-shadow 0.15s ease,
    transform 0.15s ease;

  &:hover,
  &:focus {
    outline: none;
    border-color: #2c66d5;
    box-shadow: 0px 12px 20px #2c66d526;
    transform: translateY(-2px);
  }
}

.job-card-head {
  display: flex;
  align-items: flex-start;
  gap: 0.85rem;
}

.job-card-logo {
  flex-shrink: 0;
  border-radius: 10px;
  background-color: #e6f0ff;
  color: #006cff;
  font-weight: 700;
}

.job-card-heading {
  flex: 1;
  min-width: 0;
}

.job-title {
  font-size: 1.1rem;
  font-weight: 700;
  line-height: 1.3;
  color: #111827;
  overflow-wrap: anywhere;
}

.job-card-company {
  font-size: 0.875rem;
  color: #6b7280;
  margin-top: 0.1rem;
}

.job-card-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 1rem;
}

.job-chip {
  font-size: 0.75rem;
  font-weight: 600;
  padding: 0.25rem 0.65rem;
  border-radius: 999px;
  background-color: #f3f4f6;
  color: #374151;

  &--trade {
    background-color: #e6f0ff;
    color: #006cff;
  }
}

.job-card-foot {
  display: flex;
  flex-wrap: wrap;
  align-items: baseline;
  justify-content: space-between;
  gap: 0.25rem 1rem;
  margin-top: auto;
  padding-top: 1rem;
}

.job-card-location {
  font-size: 0.875rem;
  color: #374151;
}

.job-card-pay {
  font-size: 1rem;
  font-weight: 700;
  color: #111827;

  &.is-hidden {
    font-size: 0.875rem;
    font-weight: 400;
    color: #9ca3af;
  }
}

.job-card-posted {
  margin-top: 0.35rem;
  font-size: 0.75rem;
  color: #9ca3af;
}
</style>
